import { useEffect, useCallback, useState } from "react";

/**
 * Feature 1 & 2 - Mandatory Fullscreen + Blur on Fullscreen Exit.
 *
 * Cross-browser fullscreen management. Tracks whether the document is currently
 * in fullscreen and exposes a request function. When `enabled` is false this is
 * an inert no-op that always reports fullscreen = true (so it never blurs).
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {() => HTMLElement|null} opts.getTarget  element to make fullscreen
 * @param {(entered: boolean) => void} [opts.onChange]
 */
export function useFullscreen({ enabled, getTarget, onChange } = {}) {
  const [isFullscreen, setIsFullscreen] = useState(true);

  const readFullscreenElement = useCallback(() => {
    return (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement ||
      null
    );
  }, []);

  const requestFullscreen = useCallback(() => {
    if (!enabled) return;
    try {
      const el = (getTarget && getTarget()) || document.documentElement;
      if (!el) return;
      const fn =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      if (fn) {
        const result = fn.call(el);
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [enabled, getTarget]);

  const exitFullscreen = useCallback(() => {
    try {
      const fn =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (fn && readFullscreenElement()) {
        const result = fn.call(document);
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [readFullscreenElement]);

  useEffect(() => {
    if (!enabled) {
      setIsFullscreen(true);
      return undefined;
    }

    const handleChange = () => {
      const entered = !!readFullscreenElement();
      setIsFullscreen(entered);
      if (typeof onChange === "function") onChange(entered);
    };

    const events = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];
    events.forEach((ev) => document.addEventListener(ev, handleChange, false));

    // Initialize current state.
    handleChange();

    return () => {
      events.forEach((ev) =>
        document.removeEventListener(ev, handleChange, false)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, readFullscreenElement]);

  return { isFullscreen, requestFullscreen, exitFullscreen };
}

export default useFullscreen;
